import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme,
  alpha,
  Grid,
  Tooltip,
} from '@mui/material';
import { permitAPI, projectAPI } from '../apis';
import { Permit, Project } from '../types';
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Assignment as PermitIcon,
} from '@mui/icons-material';

interface ProjectWithPermits extends Project {
  permits: Permit[];
}

const Approvals: React.FC = () => {
  const theme = useTheme();
  const [projectsWithPermits, setProjectsWithPermits] = useState<ProjectWithPermits[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjectsAndPermits = async () => {
      try {
        setError(null);
        const projectsResponse = await projectAPI.getProjects();
        if (projectsResponse.status !== 'success' || !projectsResponse.data) {
          throw new Error(projectsResponse.message || 'Failed to fetch projects');
        }

        const permitsResponse = await permitAPI.getPermits();
        if (permitsResponse.status !== 'success' || !permitsResponse.data) {
          throw new Error(permitsResponse.message || 'Failed to fetch permits');
        }

        const projectPermits = projectsResponse.data.map(project => ({
          ...project,
          permits: permitsResponse.data.filter(
            permit => permit.projectID === project.projectID && 
            (permit.status === 'pending' || permit.status === 'in_review')
          )
        })).filter(project => project.permits.length > 0);

        setProjectsWithPermits(projectPermits);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch approvals. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsAndPermits();
  }, []);

  if (loading) {
    return (
      <Box 
        sx={{ 
          pt: '84px', 
          minHeight: '100vh', 
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '84px', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ fontSize: 32, color: 'text.primary' }} />
          <Typography variant="h4">
            Approvals
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {projectsWithPermits.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: 'text.primary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Pending Approvals
            </Typography>
            <Typography color="text.secondary">
              All permits are currently up to date
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {projectsWithPermits.map((project) => (
              <Grid item xs={12} key={project.projectID}>
                <Card 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                      <BusinessIcon sx={{ color: 'text.primary' }} />
                      <Typography variant="h6">
                        {project.name}
                      </Typography>
                      <Chip 
                        icon={<PermitIcon sx={{ color: 'text.primary' }} />}
                        label={`${project.permits.length} Permits`}
                        color="default"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <List disablePadding>
                      {project.permits.map((permit, index) => (
                        <React.Fragment key={permit.permitID}>
                          <ListItem 
                            sx={{ 
                              py: 2,
                              px: 0,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={500}>
                                  {permit.permitType}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                  <Tooltip title="Submission Date">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.primary' }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {new Date(permit.submissionDate).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    •
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Stage: {permit.currentReviewStage}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </ListItem>
                          {index < project.permits.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Approvals; 